import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';

export default function MediumTermRentalsPage() {
  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/property-assessment');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#F0F0F0] to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-6">
            Service Details
          </div>
          <h1 className="font-['Fraunces'] text-5xl lg:text-6xl font-bold text-[#505A5A] mb-8">
            Medium-Term Furnished Rentals
          </h1>
          <p className="text-xl text-[#505A5A]/80 leading-relaxed max-w-3xl">
            Fully furnished rentals for 3 to 6 months. Perfect for owners who want steady income, fewer turnovers, and no short-term rental licensing requirements.
          </p>
        </div>
      </section>

      {/* Why Medium-Term Rentals */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Why choose medium-term rentals
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'No licensing required',
                description: 'Medium-term rentals (30+ days) typically do not require short-term rental licenses, making them ideal for properties that don\'t qualify for STR permits.'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Fewer turnovers',
                description: 'With stays lasting 3-6 months, you have far fewer check-ins, check-outs, and cleanings compared to short-term rentals.'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Stable income',
                description: 'Enjoy predictable monthly income without the fluctuations and seasonality of short-term rentals.'
              },
              {
                icon: 'ri-user-heart-line',
                title: 'Quality tenants',
                description: 'Medium-term rentals attract professionals, relocating families, students, and corporate travelers—reliable guests who treat your property with care.'
              },
              {
                icon: 'ri-time-line',
                title: 'Less management',
                description: 'Once a tenant is settled, there\'s minimal day-to-day management required compared to the constant communication of short-term rentals.'
              },
              {
                icon: 'ri-building-line',
                title: 'Strata-friendly',
                description: 'Many strata buildings that prohibit short-term rentals still allow medium-term furnished rentals, opening up more opportunities.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Handle */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            What we handle for you
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-image-edit-line',
                title: 'Professional listing creation',
                description: 'High-quality photos and compelling descriptions optimized for platforms like Furnished Finder, Airbnb (monthly stays), and corporate housing sites.'
              },
              {
                icon: 'ri-user-search-line',
                title: 'Tenant screening',
                description: 'We vet all applicants, conduct background checks, verify employment, and ensure you get reliable, trustworthy tenants.'
              },
              {
                icon: 'ri-file-text-line',
                title: 'Lease agreements',
                description: 'We prepare and manage all rental agreements, ensuring legal compliance and protecting your interests.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Tenant communication',
                description: 'We handle all inquiries, booking confirmations, and ongoing tenant support throughout their stay.'
              },
              {
                icon: 'ri-key-2-line',
                title: 'Move-in and move-out coordination',
                description: 'We manage check-ins, provide detailed property instructions, and conduct move-out inspections.'
              },
              {
                icon: 'ri-tools-line',
                title: 'Maintenance coordination',
                description: 'We coordinate any repairs or maintenance requests during the tenancy to keep your property in excellent condition.'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Pricing strategy',
                description: 'We set competitive monthly rates based on market demand, property features, and seasonal trends.'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Performance reporting',
                description: 'Regular updates on bookings, revenue, and property performance to keep you informed.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Who this service is ideal for
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-line',
                title: 'Secondary properties',
                description: 'Investment properties or vacation homes that don\'t qualify for short-term rental licenses'
              },
              {
                icon: 'ri-building-4-line',
                title: 'Condo owners',
                description: 'Strata buildings that prohibit short-term rentals but allow medium-term furnished rentals'
              },
              {
                icon: 'ri-time-line',
                title: 'Low-maintenance owners',
                description: 'Owners who want rental income without the constant turnover and management of short-term rentals'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Restricted zones',
                description: 'Properties in areas where short-term rentals are not permitted by zoning or bylaws'
              },
              {
                icon: 'ri-calendar-check-line',
                title: 'Seasonal availability',
                description: 'Owners who want to rent their property for part of the year while keeping it available for personal use'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Income seekers',
                description: 'Owners looking for higher returns than traditional long-term rentals without STR complexity'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-6">
            Transparent pricing
          </h2>
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <div className="text-5xl font-bold text-[#882629] mb-4">
              15%
            </div>
            <p className="text-lg text-[#505A5A]/70 mb-8">
              Commission on monthly rent • No upfront fees • No hidden costs
            </p>
            <p className="text-base text-[#505A5A]/70 mb-8">
              Our 15% commission covers all services including listing management, tenant screening, lease agreements, move-in/move-out coordination, and ongoing support.
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-[#882629] text-white px-10 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Get started with medium-term rentals
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {[
              {
                question: 'What is the typical rental period?',
                answer: 'Most medium-term rentals are 3-6 months, though some tenants may stay shorter (1-2 months) or longer (6-12 months) depending on their needs.'
              },
              {
                question: 'Do I need to furnish the property?',
                answer: 'Yes, medium-term rentals are fully furnished. This includes furniture, kitchenware, linens, towels, and all essentials. We can provide guidance on what to include.'
              },
              {
                question: 'How much can I earn compared to long-term rentals?',
                answer: 'Medium-term furnished rentals typically earn 30-50% more than traditional long-term unfurnished rentals due to the convenience and flexibility they offer tenants.'
              },
              {
                question: 'Who are typical medium-term tenants?',
                answer: 'Common tenants include corporate professionals on temporary assignments, relocating families, students, medical professionals, and people renovating their homes.'
              },
              {
                question: 'What if a tenant wants to extend their stay?',
                answer: 'Extensions are common and welcomed. We handle all lease renewals and ensure pricing remains competitive for extended stays.'
              },
              {
                question: 'Do I need special insurance?',
                answer: 'Yes, you should inform your insurance provider that you are renting your property furnished. Most insurers offer landlord or rental property policies that cover this.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {item.question}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#882629]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to earn more with less hassle?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Medium-term furnished rentals offer the perfect balance of income, stability, and simplicity. Let's get started.
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-[#882629] px-10 py-4 rounded-full text-base font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Schedule a consultation
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
